import React from 'react';
import { cn } from '../lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showInfo?: boolean;
  showFirstLast?: boolean;
  maxVisiblePages?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  showPageSizeSelector = true,
  showInfo = true,
  showFirstLast = false,
  maxVisiblePages = 5,
  className,
  size = 'md',
  variant = 'default'
}) => {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  // 사이즈별 스타일
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  // 변형별 스타일
  const getVariantClasses = (isActive = false, isDisabled = false) => {
    if (isDisabled) {
      return 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500';
    }

    if (isActive) {
      return 'bg-blue-500 text-white border-blue-500';
    }

    switch (variant) {
      case 'outline':
        return 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
      case 'ghost':
        return 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100';
      default:
        return 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
    }
  };

  // 표시할 페이지 번호들 계산
  const getVisiblePages = () => {
    const pages: (number | 'ellipsis')[] = [];
    const half = Math.floor(maxVisiblePages / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    // 끝에서 조정이 필요한 경우
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    // 첫 페이지와 ellipsis 추가
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push('ellipsis');
      }
    }

    // 중간 페이지들 추가
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // 마지막 페이지와 ellipsis 추가
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push('ellipsis');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {/* Page Size Selector */}
      {showPageSizeSelector && onPageSizeChange && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">페이지 크기:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={cn(
              'border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
              sizeClasses[size]
            )}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center space-x-2">
        {/* Info */}
        {showInfo && (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {startIndex + 1}-{endIndex} / {totalCount}
          </span>
        )}

        {/* Pagination Controls */}
        <div className="flex space-x-1">
          {/* First Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className={cn(
                'rounded transition-colors',
                sizeClasses[size],
                getVariantClasses(false, currentPage === 1)
              )}
              title="첫 페이지"
            >
              ««
            </button>
          )}

          {/* Previous */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={cn(
              'rounded transition-colors',
              sizeClasses[size],
              getVariantClasses(false, currentPage === 1)
            )}
          >
            이전
          </button>

          {/* Page Numbers */}
          {visiblePages.map((page, index) => (
            page === 'ellipsis' ? (
              <span 
                key={`ellipsis-${index}`}
                className={cn('flex items-center', sizeClasses[size])}
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={cn(
                  'rounded transition-colors',
                  sizeClasses[size],
                  getVariantClasses(page === currentPage, false)
                )}
              >
                {page}
              </button>
            )
          ))}

          {/* Next */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={cn(
              'rounded transition-colors',
              sizeClasses[size],
              getVariantClasses(false, currentPage === totalPages)
            )}
          >
            다음
          </button>

          {/* Last Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className={cn(
                'rounded transition-colors',
                sizeClasses[size],
                getVariantClasses(false, currentPage === totalPages)
              )}
              title="마지막 페이지"
            >
              »»
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { Pagination };
export type { PaginationProps };
